import React from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/Dialog';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/Select';
import { Plan } from '@/types';
import { generateServiceUrl, calculateMaxUsers, getMaxUsersLimit } from '@/utils/customerUtils';

interface CustomerCreateFormProps {
  open: boolean;
  newCustomer: {
    customer_code: string;
    alias: string;
    customer_name: string;
    max_users: number;
    service_url: string;
    contract_end_date: string;
    plan_id: number;
    contact_name: string;
    contact_email: string;
    contact_phone: string;
    notes: string;
  };
  plans: Plan[];
  mainDomain: string;
  loading: boolean;
  onCustomerChange: (customer: CustomerCreateFormProps['newCustomer']) => void;
  onSubmit: (e: React.FormEvent) => void;
  onCancel: () => void;
}

export const CustomerCreateForm: React.FC<CustomerCreateFormProps> = ({
  open,
  newCustomer,
  plans,
  mainDomain,
  loading,
  onCustomerChange,
  onSubmit,
  onCancel,
}) => {
  return (
    <Dialog open={open}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" onClose={onCancel}>
        <DialogHeader>
          <DialogTitle>새 고객사 추가</DialogTitle>
          <DialogDescription>새로운 고객사 정보를 입력하세요</DialogDescription>
        </DialogHeader>
        <div className="mt-4">
        <form onSubmit={onSubmit} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="customer_code">고객사 코드 (8자리 숫자) *</Label>
              <Input
                id="customer_code"
                value={newCustomer.customer_code}
                onChange={(e) => onCustomerChange({...newCustomer, customer_code: e.target.value})}
                placeholder="비워두면 자동 생성"
              />
              <p className="text-xs text-gray-500">8자리 숫자, 비워두면 자동 생성</p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="alias">Alias (서브도메인)</Label>
              <Input
                id="alias"
                value={newCustomer.alias}
                onChange={(e) => onCustomerChange({...newCustomer, alias: e.target.value})}
                placeholder="선택사항"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="customer_name">회사명 *</Label>
              <Input
                id="customer_name"
                value={newCustomer.customer_name}
                onChange={(e) => onCustomerChange({...newCustomer, customer_name: e.target.value})}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="plan_id">계정 유형 (플랜) *</Label>
              <Select
                value={newCustomer.plan_id ? String(newCustomer.plan_id) : ''}
                onValueChange={(value) => {
                  const planId = parseInt(value);
                  const selectedPlan = plans.find(p => p.id === planId);
                  const planName = selectedPlan?.plan_name.toLowerCase();
                  const maxUsers = calculateMaxUsers(planName, newCustomer.max_users);
                  onCustomerChange({...newCustomer, plan_id: planId, max_users: maxUsers});
                }}
                required
              >
                <SelectTrigger>
                  <SelectValue placeholder="플랜 선택" />
                </SelectTrigger>
                <SelectContent>
                  {plans.map(plan => (
                    <SelectItem key={plan.id} value={String(plan.id)}>
                      {plan.plan_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="max_users">최대 사용자 수 *</Label>
              <Input
                id="max_users"
                type="number"
                value={newCustomer.max_users}
                onChange={(e) => {
                  const plan = plans.find(p => p.id === newCustomer.plan_id);
                  const planName = plan?.plan_name.toLowerCase();
                  const limits = getMaxUsersLimit(planName);
                  let maxUsers = parseInt(e.target.value) || 1;
                  maxUsers = Math.max(limits.min, maxUsers);
                  if (limits.max !== null) {
                    maxUsers = Math.min(maxUsers, limits.max);
                  }
                  onCustomerChange({...newCustomer, max_users: maxUsers});
                }}
                min="1"
                required
                disabled={plans.find(p => p.id === newCustomer.plan_id)?.plan_name.toLowerCase() === 'personal'}
              />
              <p className="text-xs text-gray-500">
                {(() => {
                  const plan = plans.find(p => p.id === newCustomer.plan_id);
                  const planName = plan?.plan_name.toLowerCase();
                  if (planName === 'personal') {
                    return 'Personal: 1명만';
                  } else if (planName === 'professional') {
                    return 'Professional: 기본값 10명, 최대 30명';
                  } else if (planName === 'enterprise') {
                    return 'Enterprise: 기본값 30명, 무제한';
                  }
                  return '기본값: 10명';
                })()}
              </p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="service_url">서브도메인</Label>
              <Input
                id="service_url"
                value={generateServiceUrl(newCustomer.customer_code || '', newCustomer.alias, mainDomain)}
                readOnly
                className="bg-gray-100"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="contract_end_date">계약 종료일 *</Label>
              <Input
                id="contract_end_date"
                type="date"
                value={newCustomer.contract_end_date}
                onChange={(e) => onCustomerChange({...newCustomer, contract_end_date: e.target.value})}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="contact_name">담당자 이름</Label>
              <Input
                id="contact_name"
                value={newCustomer.contact_name}
                onChange={(e) => onCustomerChange({...newCustomer, contact_name: e.target.value})}
                placeholder="선택사항"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="contact_email">담당자 이메일</Label>
              <Input
                id="contact_email"
                type="email"
                value={newCustomer.contact_email}
                onChange={(e) => onCustomerChange({...newCustomer, contact_email: e.target.value})}
                placeholder="선택사항"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="contact_phone">담당자 연락처</Label>
              <Input
                id="contact_phone"
                value={newCustomer.contact_phone}
                onChange={(e) => onCustomerChange({...newCustomer, contact_phone: e.target.value})}
                placeholder="선택사항"
              />
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="notes">비고</Label>
            <textarea
              id="notes"
              value={newCustomer.notes}
              onChange={(e) => onCustomerChange({...newCustomer, notes: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-md"
              rows={4}
              placeholder="메모 및 비고사항"
            />
          </div>
          <div className="flex space-x-2">
            <Button type="submit" disabled={loading}>
              {loading ? '생성 중...' : '고객사 생성'}
            </Button>
            <Button 
              type="button" 
              variant="outline" 
              onClick={onCancel}
            >
              취소
            </Button>
          </div>
        </form>
        </div>
      </DialogContent>
    </Dialog>
  );
};

